import { useState, useCallback, useEffect } from "react";
import { cardStackProducts } from "@/mocks/homeData";

function mod(n: number, m: number): number {
  return ((n % m) + m) % m;
}

// Ring carousel: cards are placed along a circular arc at the bottom.
// The center card is active (full color, elevated), others are grayscale and rotated.
const CARD_W = 220;
const CARD_H = 320;
const RING_RADIUS = 900; // large radius so arc is subtle at bottom
const SPREAD_ANGLE = 14; // degrees between each card

export default function CardStack() {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const items = cardStackProducts;
  const n = items.length;

  const nextCard = useCallback(() => {
    setActiveIndex((prev) => (prev + 1) % n);
  }, [n]);

  const prevCard = useCallback(() => {
    setActiveIndex((prev) => (prev - 1 + n) % n);
  }, [n]);

  useEffect(() => {
    const interval = setInterval(() => {
      if (!isHovered) nextCard();
    }, 3500);
    return () => clearInterval(interval);
  }, [isHovered, nextCard]);

  // For each card, compute its position offset from center based on its slot
  const getCardTransform = (index: number) => {
    // slot: how far from active (-3 to +3 for 7 cards)
    const slot = mod(index - activeIndex + Math.floor(n / 2), n) - Math.floor(n / 2);
    const isActive = slot === 0;

    // Angle along the ring arc
    const angleDeg = slot * SPREAD_ANGLE;
    const angleRad = (angleDeg * Math.PI) / 180;

    // Position on the ring: x = R*sin(angle), y = R - R*cos(angle) (arc opens upward)
    const x = RING_RADIUS * Math.sin(angleRad);
    const y = -(RING_RADIUS * (1 - Math.cos(angleRad)));

    // The card itself rotates to follow the ring tangent
    const rotateZ = angleDeg;

    // Active card pops up more
    const extraY = isActive ? -30 : 0;

    // Scale: active is biggest
    const absSlot = Math.abs(slot);
    const scale = isActive ? 1 : Math.max(0.72, 1 - absSlot * 0.07);

    // Opacity: fade out far cards
    const opacity = isActive ? 1 : Math.max(0.35, 1 - absSlot * 0.18);

    // zIndex: active on top
    const zIndex = 100 - absSlot * 10;

    return { x, y: y + extraY, rotateZ, scale, opacity, zIndex, isActive, slot };
  };

  const activeItem = items[activeIndex];

  return (
    <div
      className="relative w-full flex flex-col items-center"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >


      {/* Ring carousel container */}
      <div
        className="relative w-full overflow-hidden"
        style={{ height: `${CARD_H + 80}px` }}
      >
        {items.map((item, index) => {
          const { x, y, rotateZ, scale, opacity, zIndex, isActive } = getCardTransform(index);

          return (
            <div
              key={item.id}
              className="absolute cursor-pointer"
              style={{
                width: CARD_W,
                height: CARD_H,
                left: "50%",
                bottom: 0,
                marginLeft: -CARD_W / 2,
                transformOrigin: "bottom center",
                transform: `translateX(${x}px) translateY(${y}px) rotateZ(${rotateZ}deg) scale(${scale})`,
                opacity,
                zIndex,
                transition: "all 0.65s cubic-bezier(0.34, 1.56, 0.64, 1)",
                filter: isActive ? "none" : "grayscale(100%) brightness(0.55)",
              }}
              onClick={() => {
                if (!isActive) {
                  setActiveIndex(index);
                }
              }}
            >
              <div
                className="w-full h-full overflow-hidden relative"
                style={{
                  background: "#161819",
                  borderRadius: "8px",
                  outline: isActive ? "1px solid rgba(255,255,255,0.07)" : "none",
                }}
              >
                {/* Card image */}
                <img
                  src={item.image}
                  alt={item.name}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />

                {/* Active card overlay with info */}
                {isActive && (
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/10 to-transparent">
                    <div className="absolute bottom-0 left-0 right-0 p-5">
                      <h3 className="text-white font-extrabold text-xl leading-tight mb-3" style={{fontFamily:"'Public Sans',sans-serif"}}>
                        {item.name}
                      </h3>
                      <div className="flex items-center gap-2 text-white/50 text-xs" style={{fontFamily:"'Space Grotesk',sans-serif"}}>
                        <span>{item.year}</span>
                        <span className="w-1 h-1 rounded-full bg-white/40" />
                        <span>{item.seasons} Releases</span>
                        <span className="w-1 h-1 rounded-full bg-white/40" />
                        <span>{item.items} Modules</span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Non-active card: show name faintly */}
                {!isActive && (
                  <div className="absolute inset-0 flex items-end p-4">
                    <p className="text-white/40 text-sm font-medium leading-tight line-clamp-2" style={{fontFamily:"'Public Sans',sans-serif"}}>
                      {item.name}
                    </p>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Navigation dots */}
      <div className="relative z-50 flex items-center gap-2 mt-8">
        <button
          onClick={prevCard}
          className="w-9 h-9 flex items-center justify-center border border-white/20 text-white/60 hover:border-white/50 hover:text-white transition-all duration-300"
          style={{borderRadius:'4px'}}
          aria-label="Previous"
        >
          <i className="ri-arrow-left-s-line text-lg" />
        </button>
        {items.map((_, i) => (
          <button
            key={i}
            onClick={() => setActiveIndex(i)}
            className={`transition-all duration-300 ${
              i === activeIndex
                ? "w-6 h-1.5 bg-[#B8422E]"
                : "w-1.5 h-1.5 rounded-full bg-white/20 hover:bg-white/40"
            }`}
            aria-label={`Go to ${i + 1}`}
          />
        ))}
        <button
          onClick={nextCard}
          className="w-9 h-9 flex items-center justify-center border border-white/20 text-white/60 hover:border-white/50 hover:text-white transition-all duration-300"
          style={{borderRadius:'4px'}}
          aria-label="Next"
        >
          <i className="ri-arrow-right-s-line text-lg" />
        </button>
      </div>
    </div>
  );
}